import { getTimestamp } from 'common/utils';
import { Action, CashRegisterState, CashMovement, CashMovementType, CashierSummary } from 'common/types';
import { db, auth } from 'firebase/config';
import { setDocWithCreatedBy } from 'firebase/utils';
import { collection, doc, addDoc, updateDoc, getDocs, query, where, orderBy, limit, CollectionReference, DocumentData } from 'firebase/firestore';

const cashRegisterCollection: CollectionReference<DocumentData> = collection(db, 'cashRegister');
const cashMovementsCollection: CollectionReference<DocumentData> = collection(db, 'cashMovements');
const cashierClosesCollection: CollectionReference<DocumentData> = collection(db, 'cashierCloses');

// Define types for the cashier actions
export type CashierCloseData = {
  date: number;
  summary: CashierSummary;
  countedCash: number;
  difference: number;
  userId: string | null;
  initialAmount: number;
  cashMovements: {
    additions: number;
    withdrawals: number;
  };
  expectedCash: number;
};

export type CashierActions = {
  fetchCashRegisterState: () => Promise<CashRegisterState | null>;
  fetchCashMovements: (registerId: string) => Promise<CashMovement[]>;
  fetchSalesData: (startTime?: number) => Promise<CashierSummary>;
  closeCashRegister: (data: CashierCloseData, registerId: string) => Promise<void>;
  openCashRegister: (initialAmount: number) => Promise<CashRegisterState>;
  addCashMovement: (type: CashMovementType, amount: number, notes: string) => Promise<CashMovement>;
};

export const createCashierActions: Action<CashierActions> = (state, updateState) => {
  // Helper function to get active register
  const getActiveRegister = async (): Promise<CashRegisterState | null> => {
    try {
      const q = query(
        cashRegisterCollection,
        where('isOpen', '==', true),
        orderBy('openDate', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const registerData = { 
          id: querySnapshot.docs[0].id, 
          ...querySnapshot.docs[0].data() 
        } as CashRegisterState;
        return registerData;
      }
      return null;
    } catch (err) {
      console.error('Error fetching cash register state:', err);
      return null;
    }
  };

  return {
    // Fetch the current active cash register
    fetchCashRegisterState: async () => {
      return await getActiveRegister();
    },

    // Fetch cash movements for a specific register
    fetchCashMovements: async (registerId: string) => {
      try {
        const q = query(
          cashMovementsCollection,
          where('registerId', '==', registerId)
        );
        
        const querySnapshot = await getDocs(q);
        const movements: CashMovement[] = [];
        
        querySnapshot.forEach((doc) => {
          movements.push({ id: doc.id, ...doc.data() } as CashMovement);
        });
        
        return movements;
      } catch (error) {
        console.error('Error fetching cash movements:', error);
        return [];
      }
    },

    // Fetch sales data for a specific time period
    fetchSalesData: async (startTime?: number) => {
      try {
        // Default to today's start timestamp (midnight) if not provided
        let startOfDay = startTime;
        if (!startOfDay) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          startOfDay = today.getTime();
        }

        // Query closed orders from the specified time
        const closedOrdersCollection = collection(db, 'closedOrders');
        const q = query(
          closedOrdersCollection,
          where('dateClose', '>=', startOfDay)
        );

        const querySnapshot = await getDocs(q);
        
        // Calculate summary
        let totalSales = 0;
        let cashSales = 0;
        let cardSales = 0;
        let orderCount = 0;

        querySnapshot.forEach((doc) => {
          const order = doc.data();
          totalSales += order.totalAmount;
          cashSales += order.cashPaymentAmount;
          cardSales += order.cardPaymentAmount;
          orderCount++;
        });

        return {
          totalSales,
          cashSales,
          cardSales,
          orderCount,
        };
      } catch (error) {
        console.error('Error fetching sales data:', error);
        throw error;
      }
    },

    // Close the cash register
    closeCashRegister: async (data: CashierCloseData, registerId: string) => {
      try {
        // Save to Firestore using setDocWithCreatedBy
        await setDocWithCreatedBy(cashierClosesCollection, data);

        // Close the cash register
        const registerRef = doc(cashRegisterCollection, registerId);
        await updateDoc(registerRef, {
          isOpen: false,
          closeDate: getTimestamp()
        });

        // Create closing cash movement
        const movement = {
          type: CashMovementType.CLOSING,
          amount: data.countedCash,
          date: getTimestamp(),
          notes: 'Cierre de caja',
          userId: auth.currentUser?.uid || null,
          registerId: registerId
        };

        await setDocWithCreatedBy(cashMovementsCollection, movement);
      } catch (error) {
        console.error('Error submitting cashier close:', error);
        throw error;
      }
    },

    // Open a new cash register
    openCashRegister: async (initialAmount: number) => {
      try {
        // Create register data with the required fields
        const registerData: any = {
          isOpen: true,
          openDate: getTimestamp(),
          initialAmount,
          currentAmount: initialAmount,
          userId: auth.currentUser?.uid || null
        };

        // Create the cash register and get the ID from setDocWithCreatedBy
        await setDocWithCreatedBy(cashRegisterCollection, registerData);
        // The ID is added to registerData by setDocWithCreatedBy
        const registerId = registerData.id;

        // Create initial cash movement
        const movement = {
          type: CashMovementType.INITIAL,
          amount: initialAmount,
          date: getTimestamp(),
          notes: 'Monto inicial',
          userId: auth.currentUser?.uid || null,
          registerId
        };

        await setDocWithCreatedBy(cashMovementsCollection, movement);

        // Return the created register with its ID
        return registerData as CashRegisterState;
      } catch (error) {
        console.error('Error opening cash register:', error);
        throw error;
      }
    },

    // Add a cash movement (addition or withdrawal)
    addCashMovement: async (type: CashMovementType, amount: number, notes: string) => {
      try {
        // Get the active register
        const activeRegister = await getActiveRegister();
        if (!activeRegister) {
          throw new Error('No active cash register found');
        }

        const movement = {
          type,
          amount,
          date: getTimestamp(),
          notes,
          userId: auth.currentUser?.uid || null,
          registerId: activeRegister.id
        };

        // Create the movement
        await setDocWithCreatedBy(cashMovementsCollection, movement);

        // Update the register's current amount
        const registerRef = doc(cashRegisterCollection, activeRegister.id);
        const newAmount = type === CashMovementType.ADDITION 
          ? activeRegister.currentAmount + amount 
          : activeRegister.currentAmount - amount;
        
        await updateDoc(registerRef, {
          currentAmount: newAmount
        });

        // Return the created movement with its ID
        return movement as CashMovement;
      } catch (error) {
        console.error('Error adding cash movement:', error);
        throw error;
      }
    },
  };
};
